/**
 * 내부 인원(직원) 피커 — CRM/ERP 의 담당 직원·담당 팀·작성자 선택 (spec 2.4).
 *
 * openPeoplePicker(anchor, {values, multi, allowNone, allowMe, dept, exclude, onChange, labels})
 *   .prop-pop.pp-pop → 검색(부분 문자열 + 한글 초성) → 부서 칩(내 부서 · 전체 · …) →
 *   목록(부서 헤더 sticky, 조직 트리 순서). 첫 행 '나에게 배정'(M), 마지막 행 '없음'.
 *   키보드: ↑↓ 이동, Enter 선택, M 나(검색어가 비었을 때), Esc 닫기, Tab 닫고 다음 요소.
 *   ≤600px 에서는 .cal-sheet 하단 시트(행 44px).
 * personEl(p, size)   personView JSON → .person DOM (partials/person.html 과 같은 마크업)
 * peopleIndex()       #peopleData 파싱 캐시 {byId, list, byDept, departments, me, meDept, ready}
 *                     — 페이지에 #peopleData 가 없을 때만 GET /api/people 을 한 번 부른다.
 * openContactPicker(anchor, {accountId, value, allowNew, onChange, labels})
 *   거래처 측 담당자(crm_contacts) 피커 — CRM 딜 패널(C8)·활동 팝오버(C3)·ERP 수신 담당자(E9)
 *   는 이 함수만 부르고 자체 담당자 목록을 만들지 않는다. GET /api/crm/contacts?account_id=…
 *   (서버 핸들러의 파라미터 이름; 응답은 [{id,name,title,account_id,…}] 배열). 행 = 이름 · 직함 · ★
 *   (주 담당자, is_primary 가 있을 때 첫 행) + mailto/tel/↗ 아이콘(email/phone 이 응답에 있을 때).
 *   allowNew 면 마지막 행 '새 담당자: <입력값>' → POST /api/crm/contacts(account_id, name) 후 선택.
 *   accountId 가 없으면 비활성 힌트 '거래처를 먼저 선택'(i18n people.pick_account_first). ≤600px 하단 시트.
 *
 * 등록: main.js 에 넣지 않는다. 쓰는 모듈(crm.js/erp.js)이 import 한다.
 */
import { userLabel, userIdentityEl } from './user-identity.js';

let openEl = null;   // .pp-pop 또는 .pp-sheet-root
let openPop = null;  // 팝 본체 (row 컨테이너를 가진 요소)
let index = null;

const CHO = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
const CHO_SET = new Set(CHO);

/** 한글 음절을 초성으로 바꾼 문자열 ("김부장" → "ㄱㅂㅈ"). 나머지 문자는 소문자 그대로. */
export function chosung(str) {
    let out = '';
    for (const ch of String(str || '')) {
        const code = ch.charCodeAt(0);
        if (code >= 0xac00 && code <= 0xd7a3) out += CHO[Math.floor((code - 0xac00) / 588)];
        else out += ch.toLowerCase();
    }
    return out;
}

function isChosungQuery(q) {
    if (!q) return false;
    for (const ch of q) if (ch !== ' ' && !CHO_SET.has(ch)) return false;
    return true;
}

/** 검색 일치: 이름·아이디·부서·직급·직책의 부분 문자열, 또는 초성만 입력하면 초성 매칭. */
export function personMatches(p, q) {
    const s = String(q || '').trim().toLowerCase();
    if (!s) return true;
    const fields = [p.name, p.username, p.dept, p.rank, p.duty, p.en];
    if (isChosungQuery(s)) {
        const want = s.replace(/\s+/g, '');
        return fields.some((f) => f && chosung(f).replace(/\s+/g, '').includes(want));
    }
    return fields.some((f) => f && String(f).toLowerCase().includes(s));
}

function buildIndex(data, prev) {
    const list = Array.isArray(data.people) ? data.people : [];
    const departments = Array.isArray(data.departments) ? data.departments : [];
    const byId = {};
    const byDept = {};
    list.forEach((p) => {
        byId[p.id] = p;
        const k = p.dept_id || '';
        (byDept[k] = byDept[k] || []).push(p);
    });
    const deptById = {};
    const children = {};
    departments.forEach((d) => {
        deptById[d.id] = d;
        const k = d.parent_id || '';
        (children[k] = children[k] || []).push(d.id);
    });
    const me = data.me || (prev && prev.me) || (document.body && document.body.dataset.meId) || '';
    const meDept = data.me_dept || (byId[me] && byId[me].dept_id) || (prev && prev.meDept) || '';
    const idx = {
        list, byId, byDept, departments, deptById, me, meDept, loaded: true,
        /** 부서 id 의 하위 트리 id 집합 (자기 자신 포함). 모르는 id → null(필터 없음). */
        subtree(id) {
            if (!id || !deptById[id]) return null;
            const out = new Set([id]);
            const queue = [id];
            while (queue.length) {
                const cur = queue.shift();
                (children[cur] || []).forEach((c) => { if (!out.has(c)) { out.add(c); queue.push(c); } });
            }
            return out;
        },
    };
    return idx;
}

/** #peopleData 파싱 캐시. 없으면 빈 인덱스를 돌려주고 /api/people 을 한 번 부른다 (index.ready). */
export function peopleIndex(force) {
    if (index && !force) return index;
    const el = document.getElementById('peopleData');
    let data = null;
    if (el) {
        try { data = JSON.parse(el.textContent || '{}'); } catch (e) { data = null; }
    }
    if (data) {
        index = buildIndex(data, index);
        index.ready = Promise.resolve(index);
        return index;
    }
    index = buildIndex({}, index);
    index.loaded = false;
    index.ready = fetch('/api/people', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : {}))
        .then((d) => { const built = buildIndex(d || {}, index); built.ready = index.ready; Object.assign(index, built); return index; })
        .catch(() => index);
    return index;
}

/** personView JSON → .person 요소. 빈 값이면 아무것도 그리지 않는 빈 span. */
export function personEl(p, size) {
    const s = document.createElement('span');
    const sz = size || 'sm';
    if (!p || !p.id) {
        s.className = 'person ' + sz + ' empty';
        return s;
    }
    // upcoming(입사 예정) 이 inactive(퇴사/비활성) 보다 우선한다 — 아직 시작 전인 계정은 꺼져 있지만 퇴사자가 아니다.
    s.className = 'person ' + sz + (p.me ? ' me' : '') + (p.upcoming ? ' upcoming' : (p.active === false ? ' inactive' : ''));
    const name = p.name || p.username || '';
    s.title = userLabel(p) + (p.rank ? ' ' + p.rank : '') + (p.dept ? ' · ' + p.dept : '') + (p.duty ? ' · ' + p.duty : '') + (p.upcoming ? ' · 입사 예정' : (p.active === false ? ' · 퇴사/비활성' : ''));
    if (p.image) {
        const img = document.createElement('img');
        img.className = 'rec-avatar';
        img.src = p.image;
        img.alt = '';
        s.appendChild(img);
    } else {
        const i = document.createElement('i');
        i.className = 'rec-avatar';
        i.textContent = p.initial || (name ? [...name.trim()][0].toUpperCase() : '?');
        s.appendChild(i);
    }
    const nameEl = document.createElement(p.href ? 'a' : 'b');
    nameEl.className = 'person-name';
    if (p.href) nameEl.href = p.href;
    nameEl.appendChild(userIdentityEl(p));
    if (p.rank) {
        nameEl.appendChild(document.createTextNode(' '));
        const r = document.createElement('span');
        r.className = 'person-rank';
        r.textContent = p.rank;
        nameEl.appendChild(r);
    }
    s.appendChild(nameEl);
    const small = document.createElement('small');
    // upcoming(입사 예정) 는 hrPeopleData 가 옵트인한 페이지에서만 내려온다.
    small.textContent = (p.dept || '') + (p.duty ? ' · ' + p.duty : '') + (p.upcoming ? ' · 입사 예정' : '');
    s.appendChild(small);
    return s;
}

let restoreFocus = null; // 피커를 연 요소 — 닫힐 때 포커스를 돌려준다 (키보드 흐름, QA ux-5)

export function closePeoplePicker() {
    if (openEl) {
        const hadFocus = openEl.contains(document.activeElement);
        openEl.remove();
        if (openEl.classList.contains('pp-sheet-root')) document.body.classList.remove('modal-scroll-lock');
        openEl = null;
        openPop = null;
        if (hadFocus && restoreFocus && restoreFocus.isConnected && restoreFocus.focus) restoreFocus.focus({ preventScroll: true });
    }
    restoreFocus = null;
}
function rememberFocus() {
    restoreFocus = document.activeElement && document.activeElement !== document.body ? document.activeElement : null;
}

export function openPeoplePicker(anchor, opts) {
    closePeoplePicker();
    const o = Object.assign({ values: [], multi: false, allowNone: true, allowMe: true, dept: '', exclude: [], onChange: null }, opts || {});
    const L = Object.assign({
        search: '이름·부서·직급·직책 검색', all: '전체', me: '나에게 배정', none: '없음',
        empty: '검색 결과 없음', loading: '불러오는 중…', nodept: '부서 없음',
    }, o.labels || {});
    const idx = peopleIndex();
    let values = (o.values || []).filter(Boolean).slice();
    const exclude = new Set((o.exclude || []).filter(Boolean));
    let dept = o.dept || '';
    const mobile = window.matchMedia && window.matchMedia('(max-width: 600px)').matches;

    const pop = document.createElement('div');
    pop.setAttribute('role', 'listbox');
    let root = pop;
    if (mobile) {
        root = document.createElement('div');
        root.className = 'pp-sheet-root';
        root.addEventListener('mousedown', (e) => { if (e.target === root) closePeoplePicker(); });
        pop.className = 'cal-sheet pp-sheet pp-pop';
        root.appendChild(pop);
    } else {
        pop.className = 'prop-pop pp-pop';
    }
    const search = document.createElement('div');
    search.className = 'pp-search';
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = L.search;
    input.autocomplete = 'off';
    input.setAttribute('aria-label', L.search);
    search.appendChild(input);
    const chips = document.createElement('div');
    chips.className = 'pp-chips';
    const list = document.createElement('div');
    list.className = 'pp-list';
    pop.appendChild(search);
    pop.appendChild(chips);
    pop.appendChild(list);

    let rows = [];   // [{kind:'me'|'none'|'person', id, el}]
    let cursor = -1;

    function emit() {
        if (o.onChange) o.onChange(values.slice());
    }
    function choose(id) {
        if (id === null) {
            values = [];
            emit();
            closePeoplePicker();
            return;
        }
        if (o.multi) {
            if (values.includes(id)) values = values.filter((x) => x !== id);
            else values.push(id);
            emit();
            const keep = cursor;
            renderList();
            setCursor(keep);
        } else {
            values = [id];
            emit();
            closePeoplePicker();
        }
    }
    function setCursor(i) {
        if (!rows.length) { cursor = -1; return; }
        cursor = Math.max(0, Math.min(rows.length - 1, i));
        rows.forEach((r, k) => r.el.classList.toggle('hi', k === cursor));
        const el = rows[cursor].el;
        if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }
    function chip(label, id) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pp-chip' + (dept === id ? ' active' : '');
        b.textContent = label;
        b.dataset.dept = id;
        b.addEventListener('click', () => { dept = id; renderChips(); renderList(); input.focus(); });
        return b;
    }
    function renderChips() {
        chips.innerHTML = '';
        const withPeople = idx.departments.filter((d) => {
            const sub = idx.subtree(d.id);
            return sub && idx.list.some((p) => sub.has(p.dept_id));
        });
        if (!withPeople.length) { chips.hidden = true; return; }
        chips.hidden = false;
        const mine = idx.meDept && idx.deptById[idx.meDept] && withPeople.some((d) => d.id === idx.meDept) ? idx.deptById[idx.meDept] : null;
        if (mine) chips.appendChild(chip(mine.name, mine.id));
        chips.appendChild(chip(L.all, ''));
        withPeople.forEach((d) => { if (!mine || d.id !== mine.id) chips.appendChild(chip(d.name, d.id)); });
        const active = chips.querySelector('.pp-chip.active');
        if (active && active.scrollIntoView) active.scrollIntoView({ inline: 'nearest', block: 'nearest' });
    }
    function row(kind, id, content) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'prop-pop-item pp-row pp-' + kind + (id && values.includes(id) ? ' sel' : '');
        b.setAttribute('role', 'option');
        if (id) b.dataset.id = id;
        b.appendChild(content);
        if (id && values.includes(id)) {
            const ck = document.createElement('span');
            ck.className = 'prop-pop-check';
            ck.textContent = '✓';
            b.appendChild(ck);
        }
        b.addEventListener('mouseenter', () => setCursor(rows.findIndex((r) => r.el === b)));
        b.addEventListener('click', () => choose(kind === 'none' ? null : id));
        list.appendChild(b);
        rows.push({ kind, id, el: b });
        return b;
    }
    function renderList() {
        list.innerHTML = '';
        rows = [];
        cursor = -1;
        const q = input.value.trim();
        if (!idx.loaded) {
            const e = document.createElement('div');
            e.className = 'pp-empty';
            e.textContent = L.loading;
            list.appendChild(e);
            return;
        }
        const sub = dept ? idx.subtree(dept) : null;
        const me = idx.byId[idx.me];
        if (o.allowMe && me && !exclude.has(me.id) && (!q || personMatches(me, q))) {
            const b = row('me', me.id, personEl(Object.assign({}, me, { dept: L.me, duty: '' }), 'md'));
            b.title = L.me + ' · ' + userLabel(me) + (me.rank ? ' ' + me.rank : '');
            const k = document.createElement('kbd');
            k.className = 'pp-kbd';
            k.textContent = 'M';
            b.appendChild(k);
        }
        const people = idx.list.filter((p) => !exclude.has(p.id) && (!sub || sub.has(p.dept_id)) && personMatches(p, q));
        let lastDept = null;
        people.forEach((p) => {
            const key = p.dept_id || '';
            if (key !== lastDept) {
                lastDept = key;
                const h = document.createElement('div');
                h.className = 'pp-head';
                h.textContent = p.dept || L.nodept;
                list.appendChild(h);
            }
            row('person', p.id, personEl(Object.assign({}, p, { me: p.id === idx.me }), 'md'));
        });
        if (!people.length) {
            const e = document.createElement('div');
            e.className = 'pp-empty';
            e.textContent = L.empty;
            list.appendChild(e);
        }
        if (o.allowNone) {
            const span = document.createElement('span');
            span.textContent = '— ' + L.none;
            row('none', '', span);
        }
        // 현재 값이 있으면 그 행에, 없으면 첫 행에 커서.
        const cur = rows.findIndex((r) => r.id && values.includes(r.id));
        setCursor(cur >= 0 ? cur : (rows.length && rows[0].kind === 'me' && rows.length > 1 && q ? 1 : 0));
    }

    input.addEventListener('input', renderList);
    input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(cursor + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(cursor - 1); }
        else if (e.key === 'Home') { e.preventDefault(); setCursor(0); }
        else if (e.key === 'End') { e.preventDefault(); setCursor(rows.length - 1); }
        else if (e.key === 'Enter') {
            e.preventDefault();
            const r = rows[cursor] || rows[0];
            if (r) choose(r.kind === 'none' ? null : r.id);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closePeoplePicker();
        } else if (e.key === 'Tab') {
            closePeoplePicker(); // 기본 동작 유지: 포커스는 다음 요소로
        } else if ((e.key === 'm' || e.key === 'M') && !input.value && o.allowMe && !e.ctrlKey && !e.metaKey && !e.altKey) {
            const me = idx.byId[idx.me];
            if (me && !exclude.has(me.id)) { e.preventDefault(); choose(me.id); }
        }
    });

    document.body.appendChild(root);
    if (mobile) {
        document.body.classList.add('modal-scroll-lock');
    } else {
        placePop(pop, anchor);
    }
    openEl = root;
    openPop = pop;
    rememberFocus();
    renderChips();
    renderList();
    input.focus();
    if (!idx.loaded && idx.ready) {
        idx.ready.then(() => { if (openPop === pop) { renderChips(); renderList(); } });
    }
    return { close: closePeoplePicker, el: pop, refresh: renderList, values: () => values.slice() };
}

// popPlacement — 피커를 어디에 열지 (HRGA 다듬기 X-9, DOM 없음 · tests/unit/people-picker-place.test.mjs).
// r = 앵커의 화면 사각형, h/w = 팝의 자연 높이·폭, vw/vh = 창 크기, navH = 머리줄 높이(--navbar-h).
// 아래(r.bottom+4)에 h 가 들어가면 아래, 아니면 위·아래 중 넓은 쪽. 어느 쪽이든 머리줄(navH) + 8 밑에서
// 시작하고, maxHeight 는 고른 쪽의 빈 높이 − 12(최소 160)라 첫 결과가 머리줄이나 창 밖에 가리지 않는다.
export function popPlacement(r, h, w, vw, vh, navH) {
    const topMin = (navH || 0) + 8;
    const left = Math.max(8, Math.min(r.left, vw - w - 8));
    const belowTop = Math.max(r.bottom + 4, topMin);
    const roomBelow = vh - belowTop - 12;
    const aboveBottom = r.top - 4;
    const roomAbove = aboveBottom - (navH || 0) - 12;
    if (h <= roomBelow || roomBelow >= roomAbove) {
        return { left, top: belowTop, maxHeight: Math.max(160, roomBelow) };
    }
    const maxHeight = Math.max(160, roomAbove);
    const top = Math.max(topMin, aboveBottom - Math.min(h, maxHeight));
    return { left, top, maxHeight };
}

function navbarHeight() {
    const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--navbar-h'));
    return Number.isFinite(v) ? v : 0;
}

// 앵커 아래에 두되 자리가 없으면 위로 뒤집는다(popPlacement). 높이는 행이 그려진 뒤(다음 프레임)에 다시
// 재서 화면 아래쪽 앵커(선택바·마지막 줄)에서도 목록이 뷰포트를 넘지 않게 한다 (Step Z 요청 23).
// maxHeight 는 CSS 의 상한(.pp-pop max-height)보다 커지지 않는다 — 넘치는 행은 .pp-list 가 스크롤한다.
function placePop(pop, anchor) {
    const r = anchor && anchor.getBoundingClientRect ? anchor.getBoundingClientRect() : { left: 8, top: 8, bottom: 8, width: 0 };
    const w = Math.max(280, Math.min(360, r.width || 0));
    pop.style.minWidth = w + 'px';
    const fit = () => {
        if (!pop.isConnected) return;
        pop.style.maxHeight = '';
        const cap = parseFloat(getComputedStyle(pop).maxHeight);
        const p = popPlacement(r, pop.offsetHeight, pop.offsetWidth, window.innerWidth, window.innerHeight, navbarHeight());
        pop.style.maxHeight = (Number.isFinite(cap) ? Math.min(cap, p.maxHeight) : p.maxHeight) + 'px';
        pop.style.left = p.left + 'px';
        pop.style.top = p.top + 'px';
    };
    fit();
    requestAnimationFrame(fit);
}

document.addEventListener('mousedown', (e) => { if (openEl && !openEl.contains(e.target)) closePeoplePicker(); }, true);
document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && openEl) { closePeoplePicker(); e.stopImmediatePropagation(); } }, true);

// ── 거래처 담당자 피커 ────────────────────────────────────────────────
function csrfToken() {
    if (window.getCSRFToken) return window.getCSRFToken();
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : '';
}

const contactCache = {}; // accountId → Promise<[contact]>

/** 거래처의 담당자 목록 (한 페이지 안에서 거래처별로 한 번만 부른다; force 로 갱신). */
export function contactsFor(accountId, force) {
    if (!accountId) return Promise.resolve([]);
    if (!force && contactCache[accountId]) return contactCache[accountId];
    const url = '/api/crm/contacts?account_id=' + encodeURIComponent(accountId) + '&account=' + encodeURIComponent(accountId);
    contactCache[accountId] = fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
        .then((list) => {
            const arr = Array.isArray(list) ? list : (list && Array.isArray(list.contacts) ? list.contacts : []);
            // 서버가 account_id 로 걸러 주지만, 필터를 모르는 구현이어도 안전하도록 한 번 더 거른다.
            const mine = arr.filter((c) => !c.account_id || c.account_id === accountId);
            mine.sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
            return mine;
        })
        .catch((e) => { delete contactCache[accountId]; throw e; });
    return contactCache[accountId];
}

/** 캐시 무효화 (담당자를 다른 경로로 만들거나 고쳤을 때). */
export function invalidateContacts(accountId) {
    if (accountId) delete contactCache[accountId];
    else Object.keys(contactCache).forEach((k) => delete contactCache[k]);
}

function contactMatches(c, q) {
    const s = String(q || '').trim().toLowerCase();
    if (!s) return true;
    const fields = [c.name, c.title, c.email, c.phone];
    if (isChosungQuery(s)) {
        const want = s.replace(/\s+/g, '');
        return fields.some((f) => f && chosung(f).replace(/\s+/g, '').includes(want));
    }
    return fields.some((f) => f && String(f).toLowerCase().includes(s));
}

/** 담당자 → 행 내용 (이름 · 직함 · ★ + mailto/tel/↗). 아이콘 클릭은 선택으로 번지지 않는다. */
function contactEl(c, L) {
    const s = document.createElement('span');
    s.className = 'person md contact';
    const i = document.createElement('i');
    i.className = 'rec-avatar';
    i.textContent = c.name ? [...String(c.name).trim()][0].toUpperCase() : '?';
    s.appendChild(i);
    const b = document.createElement('b');
    b.className = 'person-name';
    b.textContent = c.name || '';
    if (c.title) {
        b.appendChild(document.createTextNode(' '));
        const r = document.createElement('span');
        r.className = 'person-rank';
        r.textContent = c.title;
        b.appendChild(r);
    }
    if (c.is_primary) {
        const star = document.createElement('span');
        star.className = 'pp-star';
        star.textContent = ' ★';
        star.title = L.primary;
        b.appendChild(star);
    }
    s.appendChild(b);
    const small = document.createElement('small');
    small.textContent = [c.email, c.phone].filter(Boolean).join(' · ');
    s.appendChild(small);
    const icons = document.createElement('span');
    icons.className = 'pp-icons';
    const add = (href, text, title) => {
        const a = document.createElement('a');
        a.href = href;
        a.textContent = text;
        a.title = title;
        a.className = 'pp-icon';
        a.addEventListener('click', (e) => e.stopPropagation());
        a.addEventListener('mousedown', (e) => e.stopPropagation());
        icons.appendChild(a);
    };
    if (c.email) add('mailto:' + c.email, '✉', c.email);
    if (c.phone) add('tel:' + String(c.phone).replace(/[^+\d]/g, ''), '☎', c.phone);
    if (c.account_id) add('/crm/accounts/' + encodeURIComponent(c.account_id) + '#contacts', '↗', L.open);
    if (icons.childNodes.length) s.appendChild(icons);
    return s;
}

export function openContactPicker(anchor, opts) {
    closePeoplePicker();
    const o = Object.assign({ accountId: '', value: '', allowNew: false, onChange: null }, opts || {});
    const L = Object.assign({
        search: '담당자 검색', none: '없음', empty: '담당자 없음', loading: '불러오는 중…', failed: '불러오지 못했습니다',
        pickAccount: '거래처를 먼저 선택', newPrefix: '새 담당자: ', primary: '주 담당자', open: '거래처 열기',
    }, o.labels || {});
    const accountId = o.accountId || '';
    let value = o.value || '';
    const mobile = window.matchMedia && window.matchMedia('(max-width: 600px)').matches;

    const pop = document.createElement('div');
    pop.setAttribute('role', 'listbox');
    let root = pop;
    if (mobile) {
        root = document.createElement('div');
        root.className = 'pp-sheet-root';
        root.addEventListener('mousedown', (e) => { if (e.target === root) closePeoplePicker(); });
        pop.className = 'cal-sheet pp-sheet pp-pop pp-contacts';
        root.appendChild(pop);
    } else {
        pop.className = 'prop-pop pp-pop pp-contacts';
    }
    const search = document.createElement('div');
    search.className = 'pp-search';
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = L.search;
    input.autocomplete = 'off';
    input.setAttribute('aria-label', L.search);
    search.appendChild(input);
    const list = document.createElement('div');
    list.className = 'pp-list';
    pop.appendChild(search);
    pop.appendChild(list);

    let contacts = null; // null = 아직 안 옴
    let loadError = false;
    let creating = false;
    let rows = [];   // [{kind:'contact'|'none'|'new', id, el}]
    let cursor = -1;

    function emit(contact) {
        value = contact ? contact.id : '';
        if (o.onChange) o.onChange(contact || null);
    }
    function setCursor(i) {
        if (!rows.length) { cursor = -1; return; }
        cursor = Math.max(0, Math.min(rows.length - 1, i));
        rows.forEach((r, k) => r.el.classList.toggle('hi', k === cursor));
        const el = rows[cursor].el;
        if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }
    function empty(text, cls) {
        const e = document.createElement('div');
        e.className = 'pp-empty' + (cls ? ' ' + cls : '');
        e.textContent = text;
        list.appendChild(e);
        return e;
    }
    function row(kind, id, content) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'prop-pop-item pp-row pp-' + kind + (id && id === value ? ' sel' : '');
        b.setAttribute('role', 'option');
        if (id) b.dataset.id = id;
        b.appendChild(content);
        if (id && id === value) {
            const ck = document.createElement('span');
            ck.className = 'prop-pop-check';
            ck.textContent = '✓';
            b.appendChild(ck);
        }
        b.addEventListener('mouseenter', () => setCursor(rows.findIndex((r) => r.el === b)));
        b.addEventListener('click', () => activate({ kind, id, el: b }));
        list.appendChild(b);
        rows.push({ kind, id, el: b });
        return b;
    }
    function activate(r) {
        if (!r) return;
        if (r.kind === 'none') { emit(null); closePeoplePicker(); return; }
        if (r.kind === 'new') { createContact(); return; }
        const c = (contacts || []).find((x) => x.id === r.id);
        if (c) { emit(c); closePeoplePicker(); }
    }
    function createContact() {
        const name = input.value.trim();
        if (!name || creating || !accountId) return;
        creating = true;
        renderList();
        const body = new URLSearchParams({ account_id: accountId, name });
        fetch('/api/crm/contacts', {
            method: 'POST', body, credentials: 'same-origin',
            headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status))))
            .then((c) => {
                invalidateContacts(accountId);
                if (openPop === pop) { emit(Object.assign({}, c, { _created: true })); closePeoplePicker(); } // _created: 호출자가 마이크로 폼(전화·이메일)을 띄운다 (§8.4)
            })
            .catch(() => {
                creating = false;
                if (openPop === pop) { renderList(); empty(L.failed, 'pp-error'); }
            });
    }
    function renderList() {
        list.innerHTML = '';
        rows = [];
        cursor = -1;
        const q = input.value.trim();
        if (!accountId) {
            input.disabled = true;
            empty(L.pickAccount, 'pp-hint');
            return;
        }
        if (loadError) { empty(L.failed, 'pp-error'); return; }
        if (contacts === null) { empty(L.loading); return; }
        const shown = contacts.filter((c) => contactMatches(c, q));
        shown.forEach((c) => row('contact', c.id, contactEl(c, L)));
        if (!shown.length && !(o.allowNew && q)) empty(L.empty);
        if (o.allowNew && q && !shown.some((c) => String(c.name || '').trim().toLowerCase() === q.toLowerCase())) {
            const span = document.createElement('span');
            span.className = 'pp-new-label';
            span.textContent = (creating ? L.loading : L.newPrefix + q);
            const b = row('new', '', span);
            if (creating) b.disabled = true;
        }
        if (!q) {
            const span = document.createElement('span');
            span.textContent = '— ' + L.none;
            row('none', '', span);
        }
        const cur = rows.findIndex((r) => r.id && r.id === value);
        setCursor(cur >= 0 ? cur : 0);
    }

    input.addEventListener('input', renderList);
    input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(cursor + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(cursor - 1); }
        else if (e.key === 'Home') { e.preventDefault(); setCursor(0); }
        else if (e.key === 'End') { e.preventDefault(); setCursor(rows.length - 1); }
        else if (e.key === 'Enter') { e.preventDefault(); activate(rows[cursor] || rows[0]); }
        else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closePeoplePicker(); }
        else if (e.key === 'Tab') { closePeoplePicker(); }
    });

    document.body.appendChild(root);
    if (mobile) {
        document.body.classList.add('modal-scroll-lock');
    } else {
        placePop(pop, anchor);
    }
    openEl = root;
    openPop = pop;
    rememberFocus();
    renderList();
    if (accountId) {
        input.focus();
        contactsFor(accountId)
            .then((cs) => { contacts = cs; })
            .catch(() => { loadError = true; })
            .then(() => { if (openPop === pop) renderList(); });
    }
    return { close: closePeoplePicker, el: pop, refresh: renderList, value: () => value };
}
